/**
 * VqeStatementPicker — the statement switcher in the Visual Query Editor
 * workspace header. When the parsed buffer holds 2+ statements it renders a
 * shadcn <DropdownMenu> trigger that reads as a control (chevron + hover fill)
 * so the user can jump the active statement; with a single statement it
 * degrades to a plain <span> (no false affordance, no data-testid trigger) so
 * there's nothing to "open"; with no statement at all (blank buffer — the
 * starter canvas, or a comment-only buffer) it renders nothing, instead of
 * claiming "Statement 1 of 1".
 *
 * Each menu row is the statement's own text (whitespace collapsed, clamped)
 * with its leading keyword in bold — no separate kind column, which only
 * repeated that first word. The kind label stands in only when the buffer
 * text is unavailable.
 *
 * The picker is purely presentational: it reports the chosen statement id via
 * `onSelect`; the host (`VqeStatementView`) moves the shown statement
 * and clears the drill stack. Keyboard navigation + focus-return are handled by
 * Radix DropdownMenu — we keep its primitives rather than reimplementing them.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md
 *   § Statement Picker.
 */
import { Check, ChevronDown } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import type { StatementInfo } from '../../../sql-grammar/model';
import { vqeCopy } from './vqeCopy';

const PREVIEW_MAX = 40;

export interface VqeStatementPickerProps {
  readonly statements: readonly StatementInfo[];
  readonly activeStatementId: string;
  readonly onSelect: (id: string) => void;
  /** Full buffer text — used to slice a short single-line preview per item. */
  readonly bufferText?: string;
}

/**
 * Slice the statement's leading text from the buffer, collapse whitespace to
 * single spaces, and clamp to {@link PREVIEW_MAX} chars (with an ellipsis when
 * trimmed). Returns an empty string when the buffer/range is unavailable.
 */
function statementPreview(statement: StatementInfo, bufferText: string | undefined): string {
  if (!bufferText) return '';
  const start = statement.range.startOffset;
  const end = Math.min(statement.range.endOffset, start + PREVIEW_MAX * 4);
  const raw = bufferText.slice(start, end).replace(/\s+/g, ' ').trim();
  if (raw.length <= PREVIEW_MAX) return raw;
  return `${raw.slice(0, PREVIEW_MAX - 1).trimEnd()}…`;
}

/** `"SELECT a, b FROM t"` → `{ keyword: 'SELECT', rest: ' a, b FROM t' }`. */
function splitLeadingKeyword(preview: string): { keyword: string; rest: string } {
  const match = /^[A-Za-z_]+/.exec(preview);
  if (!match) return { keyword: '', rest: preview };
  return { keyword: match[0], rest: preview.slice(match[0].length) };
}

export function VqeStatementPicker({
  statements,
  activeStatementId,
  onSelect,
  bufferText,
}: VqeStatementPickerProps) {
  const count = statements.length;
  const activeIdx = statements.findIndex((s) => s.id === activeStatementId);
  const activeNum = activeIdx >= 0 ? activeIdx + 1 : 1;

  // No statement (blank / comment-only buffer) → nothing to count.
  if (count === 0) return null;

  const label = vqeCopy.workspace.statementPickerLabel(activeNum, count);

  // Single statement → no switcher, no affordance, no testid trigger.
  if (count < 2) {
    return (
      <span
        className="text-sm font-medium text-muted-foreground"
        data-statement-count={count}
        data-active-statement={activeNum}
      >
        {label}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 border border-border px-2 text-sm font-medium text-foreground/80 hover:text-foreground"
          data-testid="visual-query-statement-picker"
          data-statement-count={count}
          data-active-statement={activeNum}
        >
          {label}
          <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-w-[22rem]">
        {statements.map((statement, index) => {
          const isActive = statement.id === activeStatementId;
          const preview = statementPreview(statement, bufferText);
          const { keyword, rest } = splitLeadingKeyword(preview);
          return (
            <DropdownMenuItem
              key={statement.id}
              data-testid={`visual-query-statement-option-${index}`}
              aria-current={isActive ? 'true' : undefined}
              onSelect={() => onSelect(statement.id)}
            >
              <Check
                className={isActive ? 'opacity-100' : 'opacity-0'}
                aria-hidden
              />
              {preview ? (
                <span className="truncate text-xs" data-testid={`visual-query-statement-option-${index}-preview`}>
                  {keyword ? <span className="font-semibold text-foreground">{keyword}</span> : null}
                  <span className="text-muted-foreground">{rest}</span>
                </span>
              ) : (
                <span className="font-medium">{vqeCopy.workspace.statementKindLabel(statement.kind)}</span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
